import {
	Box,
	Dialog, 
	DialogActions, 
	DialogContent,
	DialogTitle, 
	Divider, 
	FormControl,
	FormControlLabel,
	FormHelperText,
	MenuItem,
	Stack,
	Switch,
	Typography
} from "@mui/material";

import CoAppTextField from "../../global/components/inputs/CoAppTextField";
import TiptapEditor from "../../global/components/tiptap";
import { CoAppActionButton, CoAppCancelTextButton, CoAppCloseIcon, CoAppInputLabel, CoAppSelect } from "../../global/styled/global.styled";

export default function ReactionDialog(props) {
	let reactionTypes = [...props.reactionTypes];
	const hasPopupReaction = props.currentReactions.filter(reaction => reaction.reactionType.includes("popup")).length > 0;
	const popupIsBeingEdited = props.currentReactions[props.editReactionIndex]?.reactionType.includes("popup");
	const silentIsBeingEdited = props.currentReactions[props.editReactionIndex]?.reactionType === "silent";
	/**
	 * If there is a popup type reaction configured for this rule, remove it from the reaction types
	 * This is to prevent the user from adding a popup reaction if there is already one configured
	*/
	if (hasPopupReaction && !props.isEdit) {
		reactionTypes = reactionTypes.filter(reaction => !reaction.name.includes("popup"));
	} else if (hasPopupReaction && props.isEdit && !popupIsBeingEdited) {
		reactionTypes = reactionTypes.filter(reaction => !reaction.name.includes("popup"));
	}
	/**
	 * If there is a silent reaction configured for this rule, remove it from the reaction types
	 * This is to prevent the user from adding a silent reaction if there is already one configured
	*/
	const hasSilentReaction = props.currentReactions.filter(reaction => reaction.reactionType === "silent").length > 0;
	if (hasSilentReaction && !props.isEdit) {
		reactionTypes = reactionTypes.filter(reaction => reaction.name !== "silent");
	} else if (hasSilentReaction && props.isEdit && !silentIsBeingEdited) {
		reactionTypes = reactionTypes.filter(reaction => reaction.name !== "silent");
	}

	const handleInvalidNumericValues = (e) => {
		if (e.key === "e" || e.key === "E" || e.key === "-" || e.key === "+") {
			e.preventDefault();
		}
	};

	return (
		<Dialog 
			open={props.isOpen || props.isEdit} 
			onClose={props.dialogCancelHandler}
			slotProps={{ 
				paper: {
					sx: { width: "800px", maxWidth: "90vw", overflowX: "hidden" } 
				}
			}}>
			<DialogActions sx={{ justifyContent: "space-between" }} >
				<DialogTitle>{props.isEdit ? "Edit Reaction" : "Add New Reaction"}</DialogTitle>
				<CoAppCloseIcon top={15} right={24} onClick={props.isEdit ? props.editCancelHandler : props.dialogCancelHandler}></CoAppCloseIcon>
			</DialogActions>

			<Divider sx={{ width: "100%" }} />

			<DialogContent sx={{ width: "100%", overflowX: "hidden"}}>
				<FormControl sx={{ display: "block", marginTop: "12px", marginBottom: "12px" }} error={props.typeValueError !== ""}>
					<CoAppInputLabel>Type of Reaction</CoAppInputLabel>
					<CoAppSelect
						sx={{ width: "100%" }}
						labelId="reactionType"
						id="reactionType"
						label="Type of Reaction"
						value={props.typeValue}
						onChange={props.typeDropdownValueChangeHandler}
					>
						{reactionTypes
							.slice()
							.sort((a, b) => a.friendlyName.localeCompare(b.friendlyName))
							.map(item =>
								<MenuItem key={item.name} value={item.name}>
									{item.friendlyName}
								</MenuItem>
							)}
					</CoAppSelect>

					<FormHelperText>{props.typeValueError}</FormHelperText>
				</FormControl>

				{
					props.typeValue !== "" && !["popup", "userconfirmationpopup", "managerapprovalpopup", "freeresponsepopup", "silent"].includes(props.typeValue) ?

						<FormControl sx={{ display: "block" }} error={props.fieldValueError !== ""}>
							<CoAppInputLabel id="reactionField">Reaction Field</CoAppInputLabel>
							<CoAppSelect
								sx={{ width: "100%" }}
								labelId="reactionField"
								id="reactionField"
								value={props.fieldValue}
								label="Reaction Field"
								onChange={props.fieldDropdownValueChangeHandler}
								error={props.fieldValueError !== ""}
							>
								{props.reactionFields
									.slice()
									.sort((a, b) => a.name.localeCompare(b.name))
									.map(item =>
										<MenuItem key={item.id} value={item.name}>
											{item.name}
										</MenuItem>
									)}
							</CoAppSelect>
							<FormHelperText>{props.fieldValueError}</FormHelperText>
						</FormControl>
						:
						null
				}
				{props.typeValue !== "" ?
					<Box sx={{ mt: 4, overflowX: "hidden" }}>
						<Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>Text Displayed in Reaction</Typography>
						<TiptapEditor 
							content={props.reactionText} 
							onChange={props.reactionTextInputHandler} 
						/>
						{props.reactionTextValueError && (
							<FormHelperText error>{props.reactionTextValueError}</FormHelperText>
						)}
					</Box>
					:
					null
				}
				{
					props.typeValue !== "" && ["userconfirmationpopup"].includes(props.typeValue) ?
						<Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between" sx={{ mt: 2 }}>
							<CoAppTextField
								margin="dense"
								id="reactionConfirmationText"
								label="User Confirmation Text"
								type="text"
								value={props.userConfirmationText}
								onChange={props.userConfirmationTextInputHandler}
								error={props.userConfirmationTextValueError !== ""}
								helperText={props.userConfirmationTextValueError}
								inputProps={{ maxLength: 20 }}
								sx={{ width: "30%"}}
							/>
							<Box sx={{ display: "flex", flexDirection: "column", alignItems: "left", pt: 2, gap: 1 }}>
								<FormControlLabel
									control={<Switch checked={props.showConfirmationText} onChange={props.handleShowConfirmationTextChange} />}
									label="Show on confirmation popup"
								/>
								<FormControlLabel
									control={<Switch checked={props.caseSensitive} onChange={props.handleCaseSensitiveChange} />}
									label="Case sensitive"
								/>
							</Box>
						</Stack>
						:
						null
				}
				{
					props.typeValue !== "" && props.typeValue === "freeresponsepopup" ?
						<CoAppTextField
							margin="dense"
							id="minimumCharacterCount"
							label="Minimum Character Count"
							type="number"
							fullWidth
							value={props.minimumCharacterCount}
							onChange={props.handleMinimumCharacterCountChange}
							onKeyDown={handleInvalidNumericValues} //disallows typing of non-numeric characters (E, e, -, +) that the input of type number allows by default
							onWheel={(e) => e.target.blur()} //disallows scroll via mouse wheel to change value
							inputProps={{ min: 1 }}
						/>
						:
						null
				}
			</DialogContent>
			<Divider sx={{ width: "100%" }} />
			<DialogActions>
				<CoAppCancelTextButton onClick={props.dialogCancelHandler}>Cancel</CoAppCancelTextButton>
				<CoAppActionButton disabled={props.formHasErrors} onClick={props.isEdit ? props.editSaveReactionHandler : props.saveReactionHandler}>Save</CoAppActionButton>
			</DialogActions>
		</Dialog >
	);
}